import { useState } from 'react'
import { Calendar } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { shortDate, todayCalendar, toCalendarDate, type CalendarDate } from '../../lib/dates.js'
import { Button } from './Button.js'
import { DatePicker } from './DatePicker.js'
import { Popover } from './Popover.js'
import { Field } from './Field.js'
import { Stack, Row } from '../layout/Stack.js'
import { Text } from './Typography.js'

export interface DateRange {
  from: CalendarDate | null
  to: CalendarDate | null
}

export interface DateRangePickerProps {
  value: DateRange
  onChange: (value: DateRange) => void
  /** The shortcuts offered above the two fields. */
  presets?: boolean
  disabled?: boolean
  id?: string
}

/** The stretches a desk actually asks for, rather than a calendar to count on. */
const PRESETS: [string, () => DateRange][] = [
  ['Today', () => ({ from: todayCalendar(), to: todayCalendar() })],
  ['The last 7 days', () => back(7)],
  ['The last 30 days', () => back(30)],
  [
    'This month',
    () => {
      const now = new Date()
      return {
        from: toCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: todayCalendar(),
      }
    },
  ],
]

function back(days: number): DateRange {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  return { from: toCalendarDate(from), to: toCalendarDate(to) }
}

/**
 * From and to, as one control.
 *
 * Four admin endpoints take a `from` and a `to` — transactions, bookings, the
 * audit log, the exports. Two separate date fields is the arrangement where
 * somebody sets the end before the start and gets an empty table with no
 * explanation, so the two ends are held together here and **neither can cross the
 * other**: choosing a start after the current end moves the end with it.
 *
 * The presets are there because "the last 30 days" is what a desk asks for, and
 * counting back thirty days on a calendar grid is not a thing anybody should do
 * at work.
 */
export function DateRangePicker({
  value,
  onChange,
  presets = true,
  disabled,
  id,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const label =
    value.from && value.to
      ? `${shortDate(value.from)} – ${shortDate(value.to)}`
      : value.from
        ? `From ${shortDate(value.from)}`
        : value.to
          ? `Until ${shortDate(value.to)}`
          : 'Any time'

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={cn(
            'flex h-10 items-center gap-2 rounded-md border border-hairline-strong bg-surface px-4 text-body',
            'outline-none transition-colors focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
            'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
            !value.from && !value.to && 'text-ink-muted',
          )}
        >
          <Calendar className="size-4 shrink-0 text-ink-muted" aria-hidden />
          {label}
        </button>
      }
    >
      <Stack gap={4} className="w-72">
        {presets && (
          <Row wrap gap={2}>
            {PRESETS.map(([name, make]) => (
              <Button key={name} variant="secondary" size="sm" onClick={() => onChange(make())}>
                {name}
              </Button>
            ))}
          </Row>
        )}
        <Field label="From">
          {(f) => (
            <DatePicker
              {...f}
              clearable
              value={value.from}
              max={value.to ?? undefined}
              onChange={(from) =>
                onChange({ from, to: value.to && from && value.to < from ? from : value.to })
              }
            />
          )}
        </Field>
        <Field label="To">
          {(f) => (
            <DatePicker
              {...f}
              clearable
              value={value.to}
              min={value.from ?? undefined}
              onChange={(to) => onChange({ from: value.from, to })}
            />
          )}
        </Field>
        {(value.from || value.to) && (
          <Button variant="tertiary" size="sm" onClick={() => onChange({ from: null, to: null })}>
            Any time
          </Button>
        )}
        <Text variant="hint">Both ends are included.</Text>
      </Stack>
    </Popover>
  )
}
